/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  UI_OPERATION_STATUS_REQUEST,
} from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormCheckbox as Checkbox,
  FormEmail as EmailField,
  FormTextField as TextField,
} from '@uncinc/uncinc-react-kitchen-sink';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';

import { Button, PasswordField } from '../../../components';
import { MINIMUM_PASSWORD_CHARACTER_COUNT } from '../../../config/constants';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import useOperationStatus from '../../../hooks/useOperationStatus';

// Temporary. See file for more information.
import { userRegisterWithPassword } from '../../../store/actions/Login';
import useDispatch from '../../../hooks/useTypedDispatch';

const OnboardingForm: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { privacy, joyride } = useRoutes();
  const { buildClass } = useClasses('PageOnboarding__form');
  const history = useHistory();

  const isLoggedIn = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['login', 'loggedIn'], null));

  React.useEffect(() => {
    if (isLoggedIn) {
      history.push(joyride);
    }
  }, [history, isLoggedIn, joyride]);

  const registerOperationStatus = useOperationStatus('user', 'register');

  const handleSubmit = ({ fullName, email, password }) => {
    dispatch(userRegisterWithPassword(email, password, fullName));
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .trim()
      .required(t('pages.onboarding_credentials_default.fields.fullName.validation.required')),
    email: Yup.string()
      .email(t('pages.onboarding_credentials_default.fields.email.validation.email'))
      .required(t('pages.onboarding_credentials_default.fields.email.validation.required')),
    password: Yup.string()
      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t('pages.reset_password.fields.password.validation.min', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }))
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('pages.reset_password.fields.password.validation.invalid'))
      .required(t('pages.onboarding_credentials_default.fields.password.validation.required')),
    privacy: Yup.boolean()
      .isTrue(t('pages.onboarding_credentials_default.fields.privacy.validation.required'))
      .required(t('pages.onboarding_credentials_default.fields.privacy.validation.required')),
  });

  return (
    <FormikWrapper
      initialValues={{
        fullName: '',
        email: '',
        password: '',
        privacy: false,
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        handleSubmit(values);
        setSubmitting(false);
      }}
    >
      <TextField
        fieldName="fullName"
        title={t('pages.onboarding_credentials_default.fields.fullName.label')}
        placeholder={t('pages.onboarding_credentials_default.fields.fullName.placeholder')}
      />
      <EmailField
        fieldName="email"
        title={t('pages.onboarding_credentials_default.fields.email.label')}
        placeholder={t('pages.onboarding_credentials_default.fields.email.placeholder')}
      />
      <PasswordField
        fieldName="password"
        title={t('pages.onboarding_credentials_default.fields.password.label')}
      />
      <Checkbox
        fieldName="privacy"
        title={(
          <Trans i18nKey="pages.onboarding_credentials_default.fields.privacy.label">
            {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
            <a href={privacy} target="_blank" rel="noreferrer" className="textual-link" />
          </Trans>
        )}
      />
      <div className={buildClass('buttons')}>
        <Button
          className={buildClass('submit')}
          htmlType="submit"
          buttonState={registerOperationStatus === UI_OPERATION_STATUS_REQUEST ? 'disabled' : undefined}
        >
          {t('pages.onboarding_credentials_default.submit')}
        </Button>
      </div>
    </FormikWrapper>
  );
};

export default OnboardingForm;
